// Next
import dynamic from "next/dynamic";

// MUI
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// Component
import SeoHead from "../../components/SeoHead";
import PartDisplay from "../../components/PartDisplay";

// Functions
import api from "../../functions/api";


// Bottom of Page Content
// const BlogComments = dynamic(() => import("../../components/BlogComments"));
const SearchTool = dynamic(() => import("../../components/SearchTool"));
// const ArticleRecommendations = dynamic(() => import("../../components/ArticleRecommendations"));
const SuggestBlogPostsHorizontal = dynamic(() => import("../../components/SuggestBlogPostsHorizontal"));
const EmailSignupArticleEmbed = dynamic(() => import("../../components/EmailSignupArticleEmbed"));
const SocialShareButtons = dynamic(() => import("../../components/SocialShareButtons"));
const EmailSignupPopup = dynamic(() => import("../../components/EmailSignupPopup"));


// Styles
const breadcrumbsHeader = {
  textAlign: "left",
  marginTop: "1vh",
  marginBottom: "1vh",
  paddingLeft: "6vw",
  paddingRight: "6vw",
};

const contentBody = {
  textAlign: "left",
  marginTop: "3vh",
  marginBottom: "2vh",
  minHeight: "100vh",
  paddingLeft: "6vw",
  paddingRight: "6vw",
};


const h2Titles = {
  color: "text.secondary",
  fontSize: "2.5rem",
  marginBottom: "2vh",
};

const searchContainer = {
  width: "100%",
  backgroundColor: "background.secondary",
  textAlign: "center",
  align: "center",
  justify: "center",
  margin: "0",
  color: "text",
  fontSize: "2.5rem",
  padding: "2vh",
  marginTop: "8vh",
  marginBottom: "5vh",
  paddingTop: "5vh",
  paddingBottom: "3vh",
  WebkitBoxShadow: "2px 2px 15px rgba(0,0,0,0.4)",
  MozBoxShadow: "2px 2px 15px rgba(0,0,0,0.4)",
};

const bottomPageContentContainer = {
  paddingTop: "5vh",
  paddingBottom: "3vh",
};

const comments = {
  textAlign: "center",
  align: "center",
  justify: "center",
  fontSize: "2.5rem",
  paddingTop: "5vh",
  paddingBottom: "3vh",
};

const articleSuggestionsContainer = {
  width: "100%",
  backgroundColor: "background",
  textAlign: "center",
  align: "center",
  justify: "center",
  color: "text",
  fontSize: "2.5rem",
  paddingTop: "5vh",
  paddingBottom: "5vh",
};

const shareText = {
  marginTop: "4vh",
  fontSize: 22,
};


function Parts({ part, partData }) {
  const body = {
    textAlign: "center",
    paddingTop: "40px",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    fontSize: "calc(10px + 2vmin)",
  };

  const canonicalLink = `https://searchexhaust.com/parts/${encodeURIComponent(part)}`
  const uniqueBrandAndPartName = `${partData["Brand"]}-${partData["Name"]}`

  // ToDO: Parse rating out of data
  // const JsonLdSku = (partData["SKU"]) && `"sku": "${partData["SKU"]}",`
  const JsonLdSku = partData["SKU"] ? `"sku": "${partData["SKU"]}",` : '';
  const JsonLdMpn = partData["SKU"] ? `"mpn": "${partData["SKU"]}",` : '';
  const JsonLdImages = partData["images"]
    ? `"image": [${partData["images"].map((url) => `"${url}"`).join(", ")}],`
    : '';
  const JsonLdOffers = partData["Price"] ? `
    "offers": {
      "@type": "Offer",
      "url": "${canonicalLink}",
      "priceCurrency": "USD",
      "price": ${partData["Price"]},
      "itemCondition": "https://schema.org/NewCondition",
      "availability": "https://schema.org/InStock"
    },` : '';

  function addProductJsonLd() {
    return {
      __html: `
    {
      "@context": "https://schema.org/",
      "@type": "Product",
      "name": "${partData["Name"]}",
      ${JsonLdImages}
      "description": "${partData["Description"]}",
      ${JsonLdSku}
      ${JsonLdMpn}
      ${JsonLdOffers}
      "brand": {
        "@type": "Brand",
        "name": "${partData["Brand"]}"
      }
    }
  `,
    };
  }

  return (
    <div>
      <SeoHead
        title={`${partData["Brand"]} - ${part} `}
        description={`${part} is the part for you! Find our all the details you need about this aftermarket exhaust component.`}
        canonicalLink={canonicalLink}
        jsonLd={addProductJsonLd()}
      />
      <Container className="body" sx={body} maxWidth="false">
        <PartDisplay
          name={partData["Name"]}
          brand={partData["Brand"]}
          link={partData["productLink"]}
          part={partData}
        />
        <Box sx={searchContainer}>
          <Container maxWidth="sm">
            <Typography variant="h2" sx={h2Titles}>
              Discover Your Perfect Exhaust!
            </Typography>
            <Typography>
            Effortlessly Explore the Best Aftermarket Exhaust Solutions for Your Vehicle.
            </Typography>
          </Container>
          <SearchTool />
        </Box>
      </Container>
      <Container sx={bottomPageContentContainer}>
        <hr />
        <Container>
          <EmailSignupArticleEmbed page={canonicalLink} />
          <Typography variant="body1" sx={shareText}>
          Connect and Share This Product with Your Network
          </Typography>
          <SocialShareButtons url={canonicalLink} title={uniqueBrandAndPartName} />
        </Container>
        <Box sx={articleSuggestionsContainer}>
          <Typography variant="h2" sx={h2Titles}>
            Our Most Popular Articles Right Now
          </Typography>
          <SuggestBlogPostsHorizontal />
        </Box>
        <EmailSignupPopup delaySeconds={30} page={canonicalLink} />
      </Container>
    </div >
  );
}

async function getAllParts() {
  const ddbCallCacheData = await api.cache.get();

  let partsPathsList = [];
  // https://stackoverflow.com/questions/66270207/next-js-how-can-i-make-dynamic-routes-with-spaces-work?noredirect=1&lq=1

  Object.keys(ddbCallCacheData["parts"]).map((part) => {
    const urlPartPath = encodeURIComponent(part);
    partsPathsList.push({
      params: {
        part: part,
      },
    });
  });

  return partsPathsList;
}

export async function getStaticPaths() {

  await api.cache.checkCacheExists()

  const paths = await getAllParts();
  return {
    paths,
    fallback: false,
  };
}

export async function getStaticProps({ params }) {
  const ddbCallCacheData = await api.cache.get();
  const part = decodeURIComponent(params.part)
  const partData = ddbCallCacheData["parts"][part];

  return {
    props: {
      part,
      partData,
    },
  };
}

export default Parts;
